import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { DotsVerticalIcon, PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { useTranslation } from "@refinedev/core";
import { DropdownMenu } from "components/dropdownMenu";
import Input from "components/input";
import { Label } from "components/label";
import { IAttendantTemplateInputItem, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

interface IOptionFormProps {
  templateIndex: number;
  groupIndex: number;
  fieldIndex: number;
  languages: SupportedLanguage[];
}

export const OptionForm = ({ templateIndex, groupIndex, fieldIndex, languages }: IOptionFormProps) => {
  const { translate } = useTranslation();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<{fields: IAttendantTemplateInputItem[]}>(); // retrieve all hook methods

  const { fields, move, append, remove } = useFieldArray({
    control,
    name: `fields.${fieldIndex}.option`,
  });

  const handleDrag = ({ source, destination }: any) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  return (
    <div className="mt-4 bg-magnolia-600 px-5 py-4 -mx-4 sm:-mx-6 -mb-4 rounded-bl-lg rounded-br-lg">
      <p className="text-sm mb-2 font-medium">{translate("eventSetup.participantTemplate.field.optionValue")}</p>
      <DragDropContext onDragEnd={handleDrag}>
        <div className="grid sm:grid-cols-6 lg:gap-x-6">
          <Droppable droppableId="option-items">
            {(provided, snapshot) => (
              <div
                className="col-span-full"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {(fields ?? []).map((field: any, index: number) => (
                  <Draggable
                    key={`option[${index}]`}
                    draggableId={`item-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        className={`border rounded-lg mb-4 ${
                          get(errors, `fields.${fieldIndex}.option.${index}`, "")
                            ? "border-red-500"
                            : "border-brand-500"
                        }`}
                        key={field.id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div
                          className="bg-magnolia-600 rounded-lg"
                          {...provided.dragHandleProps}
                        >
                          <div className="px-4 py-4 flex flex-col gap-y-4">
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <Label
                                  required
                                  id={`label_${index}`}
                                  name={translate("eventSetup.participantTemplate.field.valueName")}
                                />
                                <div className="flex flex-row items-center justify-start gap-x-2">
                                  {fields.length > 1 ? (
                                    <>
                                      <DropdownMenu
                                        label={
                                          <DotsVerticalIcon className="w-4 h-4 text-[#4F5559]" />
                                        }
                                        items={[
                                          ...(index !== 0
                                            ? [
                                                {
                                                  label: "Move up",
                                                  onClick: () =>
                                                    move(index, index - 1),
                                                },
                                              ]
                                            : []),
                                          ...(index !== fields.length - 1
                                            ? [
                                                {
                                                  label: "Move Down",
                                                  onClick: () =>
                                                    move(index, index + 1),
                                                },
                                              ]
                                            : []),
                                        ]}
                                      />
                                      <div className="mx-1 self-center h-5 w-[1px] bg-black"></div>
                                    </>
                                  ) : <></>}
                                  <button onClick={() => remove(index)}>
                                    <TrashIcon className="w-5 h-5 text-[#4F5559]" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex flex-col gap-y-4">
                                {languages.map(
                                  (lang: SupportedLanguage) => (
                                    <div key={lang}>
                                      <Controller
                                        name={`fields.${fieldIndex}.option.${index}.label.${lang}`}
                                        control={control}
                                        render={({ field }: any) => (
                                          <div className="relative">
                                            <Input
                                              id={`label_${index}`}
                                              type="text"
                                              placeholder="Label"
                                              className="bg-white pr-11"
                                              error={get(
                                                errors,
                                                `fields.${fieldIndex}.option.${index}.label.${lang}.message`,
                                                ""
                                              )}
                                              {...field}
                                            />
                                            <img
                                              src={`/images/flags/circle-${lang}.png`}
                                              alt="Language Icon"
                                              className="absolute top-1/2 right-3 -translate-y-1/2"
                                            />
                                          </div>
                                        )}
                                      />
                                      {get(
                                        errors,
                                        `fields.${fieldIndex}.option.${index}.label.${lang}`,
                                        ""
                                      ) ? (
                                        <p className="error-text">
                                          <span className="font-medium">
                                            {get(
                                              errors,
                                              `fields.${fieldIndex}.option.${index}.label.${lang}.message`,
                                              ""
                                            ) as string}
                                          </span>
                                        </p>
                                      ) : null}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>

                            <div>
                              <Label
                                required
                                id={`key_${index}`}
                                name="Key"
                              />
                              <div>
                                <Controller
                                  name={`fields.${fieldIndex}.option.${index}.key`}
                                  control={control}
                                  render={({ field }: any) => (
                                    <div className="relative">
                                      <Input
                                        id={`key_${index}`}
                                        type="text"
                                        placeholder="Key"
                                        className="bg-white"
                                        error={get(
                                          errors,
                                          `fields.${fieldIndex}.option.${index}.key.message`,
                                          ""
                                        )}
                                        {...field}
                                      />
                                    </div>
                                  )}
                                />
                                {get(
                                  errors,
                                  `fields.${fieldIndex}.option.${index}.key`,
                                  ""
                                ) ? (
                                  <p className="error-text">
                                    <span className="font-medium">
                                      {get(
                                        errors,
                                        `fields.${fieldIndex}.option.${index}.key.message`,
                                        ""
                                      ) as string}
                                    </span>
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      <div className="w-full">
        <button
          type="button"
          className="flex flex-row items-center w-fit py-2 text-sm text-brand-500 active:opacity-85"
          onClick={() =>
            append({
              key: "",
              label: {
                en: "",
                vi: ""
              }
            } as never)
          }
        >
          <PlusIcon className="w-4 h-4 mr-1.5" />{" "}
          <span className="shrink-0 text-sm font-medium">{translate("eventSetup.participantTemplate.field.addMoreOption")}</span>
        </button>
      </div>
    </div>

  )
}